import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe as useStripeWeb, useElements } from '@stripe/react-stripe-js';
import { TreeItem, BasketItem } from '../types';

export type { TreeItem, BasketItem };

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_mock_holder_key');

interface BasketSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    basket: BasketItem[];
    updateQuantity: (id: string, increment: boolean) => void;
    removeFromBasket: (id: string) => void;
    handleCheckout: () => void;
    checkoutLoading: boolean;
    clientSecret: string | null;
    setClientSecret: (val: string | null) => void;
    handlePaymentSuccess: () => void;
    grandTotalPrice: number;
}

const EmbeddedCheckoutForm = ({ clientSecret, amount, onSuccess, onCancel }: { clientSecret: string; amount: number; onSuccess: () => void; onCancel: () => void }) => {
    const stripe = useStripeWeb();
    const elements = useElements();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setIsProcessing(true);
        setErrorMessage(null);

        const { error: submitError } = await elements.submit();
        if (submitError) {
            setErrorMessage(submitError.message || "Form validation failed.");
            setIsProcessing(false);
            return;
        }

        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                clientSecret,
                redirect: 'if_required'
            });

            if (error) {
                setErrorMessage(error.message || "An unexpected error occurred.");
            } else if (paymentIntent && paymentIntent.status === 'succeeded') {
                onSuccess();
            }
        } catch (err: any) {
            setErrorMessage(err.message || "Handshake execution failure.");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-white border border-gray-100 rounded-2xl shadow-inner text-left">
            <PaymentElement />
            {errorMessage && <div className="text-red-500 font-semibold text-xs bg-red-50 p-2 rounded-lg">{errorMessage}</div>}
            <div className="flex gap-2 pt-2">
                <button type="button" onClick={onCancel} disabled={isProcessing} className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-black py-2.5 rounded-xl text-sm disabled:opacity-50">
                    Cancel
                </button>
                <button type="submit" disabled={!stripe || isProcessing} className="w-full bg-green-600 hover:bg-green-700 text-white font-black py-2.5 rounded-xl text-sm flex justify-center items-center gap-2 disabled:opacity-50">
                    {isProcessing ? "Processing..." : `Pay $${amount.toFixed(2)}`}
                </button>
            </div>
        </form>
    );
};

const BasketSidebar: React.FC<BasketSidebarProps> = ({
    isOpen, onClose, basket, updateQuantity, removeFromBasket, handleCheckout, 
    checkoutLoading, clientSecret, setClientSecret, handlePaymentSuccess, grandTotalPrice
}) => {
    if (!isOpen) return null;

    const totalItemsCount = basket.reduce((acc, item) => acc + item.quantity, 0);

    return (
        <div className="fixed inset-0 bg-black/50 z-[100] backdrop-blur-xs flex justify-end text-left">
            <div className="absolute inset-0" onClick={() => { onClose(); setClientSecret(null); }}></div>
            <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col justify-between z-10 animate-in slide-in-from-right duration-200">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <div>
                        <h2 className="text-2xl font-black text-gray-900 tracking-tight" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>Your Basket</h2>
                        <p className="text-xs font-semibold text-gray-500 mt-0.5">{totalItemsCount} items selected</p>
                    </div>
                    <button onClick={() => { onClose(); setClientSecret(null); }} className="p-2 rounded-full text-gray-400 hover:text-gray-700">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                
                <div className="flex-grow overflow-y-auto p-4 space-y-4">
                    {clientSecret ? (
                        <div className="space-y-4 pt-2">
                            <h3 className="text-sm font-bold text-gray-400 tracking-wider uppercase text-left">Secure Card Checkout</h3>
                            <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
                                <EmbeddedCheckoutForm clientSecret={clientSecret} amount={grandTotalPrice} onSuccess={handlePaymentSuccess} onCancel={() => setClientSecret(null)} />
                            </Elements>
                        </div>
                    ) : basket.length === 0 ? (
                        <div className="text-center py-20 text-gray-400 font-medium">Your Basket is completely empty.</div>
                    ) : (
                        <div className="divide-y divide-gray-50">
                            {basket.map((item) => (
                                <div key={item.tree._id} className="flex items-center justify-between gap-4 pt-2 pb-2">
                                    <div className="flex items-center gap-3">
                                        <img src={item.tree.imageUrl?.startsWith('http') ? item.tree.imageUrl : (process.env.REACT_APP_API_URL || 'http://localhost:3000') + item.tree.imageUrl} alt={item.tree.name} className="w-14 h-14 object-cover rounded-xl border border-gray-100" />
                                        <div>
                                            <h4 className="font-extrabold text-gray-900 text-base text-left">{item.tree.name}</h4>
                                            <p className="text-xs font-semibold text-green-600 text-left mt-0.5">${item.tree.price} each</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center border border-gray-200 rounded-full p-1 bg-gray-50">
                                            <button onClick={() => updateQuantity(item.tree._id, false)} className="w-6 h-6 flex items-center justify-center bg-white rounded-full text-gray-600 font-bold text-xs">-</button>
                                            <span className="w-8 font-extrabold text-gray-900 text-center text-sm">{item.quantity}</span>
                                            <button onClick={() => updateQuantity(item.tree._id, true)} className="w-6 h-6 flex items-center justify-center bg-white rounded-full text-gray-600 font-bold text-xs">+</button>
                                        </div>
                                        <button onClick={() => removeFromBasket(item.tree._id)} className="p-1.5 text-white bg-red-500 hover:bg-red-600 rounded-full transition-colors" aria-label="Remove item">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                
                {!clientSecret && (
                    <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="font-extrabold text-gray-600 text-base">Total:</span>
                            <span className="text-2xl font-black text-green-600">${grandTotalPrice.toFixed(2)}</span>
                        </div>
                        <button onClick={handleCheckout} disabled={basket.length === 0 || checkoutLoading} className="w-full bg-green-600 text-white font-black py-3 rounded-xl text-base hover:bg-green-700 shadow-lg disabled:opacity-40 flex justify-center items-center gap-2">
                            {checkoutLoading ? "Connecting..." : "Checkout"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BasketSidebar;